import { camelize } from '@ember/string';
import Component from '@glimmer/component';
import { or } from 'ember-truth-helpers';
import { getFormattedLegacyRoleName, type LegacyRole } from 'garaje/utils/custom-roles';

interface AdminEventDataSignature {
  Args: {
    eventType: string;
    eventData: string;
    formatValue: (value: unknown) => string;
  };
}

interface AdminEventDataType {
  roleName?: string;
  customRoleName?: string;
  previousRole?: string;
  previousCustomRoleName?: string;
  roleScopeType?: string;
  roleScopeName?: string;
  companyName?: string;
  locationName?: string;
  manuallyAdded?: boolean;
  confirmedAt?: string;
}

export default class AdminEventData extends Component<AdminEventDataSignature> {
  get eventData(): AdminEventDataType {
    try {
      const parsedData = <Record<string, unknown>>JSON.parse(this.args.eventData);
      const data = Object.fromEntries(Object.entries(parsedData).map(([key, value]) => [camelize(key), value]));

      return data as AdminEventDataType;
    } catch {
      return {};
    }
  }

  get adminRole(): string {
    const { roleName, customRoleName } = this.eventData;
    if (roleName) {
      return getFormattedLegacyRoleName(roleName as LegacyRole);
    } else if (customRoleName) {
      return customRoleName;
    } else {
      return '-';
    }
  }

  get previousAdminRole(): string {
    const { previousRole, previousCustomRoleName } = this.eventData;
    if (previousRole) {
      return getFormattedLegacyRoleName(previousRole as LegacyRole);
    } else if (previousCustomRoleName) {
      return previousCustomRoleName;
    } else {
      return '-';
    }
  }

  get adminRoleRemoved(): boolean {
    return this.args.eventType.endsWith('.removed');
  }

  <template>
    {{#if this.eventData}}
      <div class="divide-y divide-carbon-10" data-test-admin-event-data>
        <div class="py-3 first:pt-0 last:pb-0">
          <div class="text-sm text-carbon-40 mb-1">Admin role</div>
          <div class="text-sm text-carbon-70">
            {{#if (or this.eventData.previousRole this.eventData.previousCustomRoleName)}}
              <span class="text-carbon-40">{{this.previousAdminRole}}</span>
              <span class="text-gem-50">&rarr;</span>
              {{this.adminRole}}
            {{else if this.adminRoleRemoved}}
              <s>{{this.adminRole}}</s>
            {{else}}
              {{this.adminRole}}
            {{/if}}
          </div>
        </div>

        {{#if this.eventData.roleScopeType}}
          <div class="py-3 first:pt-0 last:pb-0">
            <div class="text-sm text-carbon-40 mb-1">Role scope</div>
            <div class="text-sm text-carbon-70">
              {{@formatValue this.eventData.roleScopeName}}
              ({{@formatValue this.eventData.roleScopeType}})
            </div>
          </div>
        {{/if}}

        {{#if this.eventData.companyName}}
          <div class="py-3 first:pt-0 last:pb-0">
            <div class="text-sm text-carbon-40 mb-1">Company</div>
            <div class="text-sm text-carbon-70">
              {{@formatValue this.eventData.companyName}}
            </div>
          </div>
        {{/if}}

        {{#if this.eventData.locationName}}
          <div class="py-3 first:pt-0 last:pb-0">
            <div class="text-sm text-carbon-40 mb-1">Location</div>
            <div class="text-sm text-carbon-70">
              {{@formatValue this.eventData.locationName}}
            </div>
          </div>
        {{/if}}

        <div class="py-3 first:pt-0 last:pb-0">
          <div class="text-sm text-carbon-40 mb-1">Manually added</div>
          <div class="text-sm text-carbon-70">
            {{@formatValue this.eventData.manuallyAdded}}
          </div>
        </div>

        <div class="py-3 first:pt-0 last:pb-0">
          <div class="text-sm text-carbon-40 mb-1">Confirmed at</div>
          <div class="text-sm text-carbon-70">
            {{@formatValue this.eventData.confirmedAt}}
          </div>
        </div>
      </div>
    {{else}}
      <div class="text-center text-carbon-50 py-4">
        No admin role changes available
      </div>
    {{/if}}
  </template>
}
